import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import dayjs from 'dayjs'
import specializedReportService, { specializedAttachFormats } from '@api/v1/specializedReportService'
import reportJobService from '@api/v1/reportJobService'
import useLatestRun from '@hooks/useLatestRun'
import SpecializedReportShell from '@components/reports/SpecializedReportShell'
import SpecializedFilterBar from '@components/reports/SpecializedFilterBar'
import { objectField, dateRangeField, choiceField, boolField } from '@components/reports/specializedFilterFields'
import ReportViewsMenu from '@components/reports/ReportViewsMenu'
import useSavedReportView from '@components/reports/useSavedReportView'
import { describeFilters, dateRangePart } from '@components/reports/reportFilterSummary'
import CsvReportGrid from '@components/reports/CsvReportGrid'
import useUsePosting from '@hooks/useUsePosting'

const TYPE_USERS = 1
const TYPE_COMPANIES = 3

const POSTED_OPTIONS = [
  { value: 'all', label: "Don't filter" },
  { value: 'posted', label: 'Posted' },
  { value: 'unposted', label: 'Unposted' },
]

const makeDefaults = () => ({
  companyId: null, companyName: '',
  userId: null, userName: '',
  invoiceDateStart: '', invoiceDateEnd: '',
  paymentReceivedDateStart: dayjs().startOf('month').format('YYYY-MM-DD'),
  paymentReceivedDateEnd: dayjs().format('YYYY-MM-DD'),
  isSummaryReport: true,
  postedFilter: null, // null | true | false
  useHistoricalTitles: false,
  useHistoricalOrigination: false,
})

/**
 * Built per render because the Posted control is TENANT-GATED: V1 hides
 * `#postedFilterDiv` entirely on non-posting tenants, so the field must not exist
 * in the bar at all there (not merely be disabled).
 *
 * ⚠️ Neither date range sets `omitAll`. That is deliberate and matches V1: the
 * Invoice Date window opens at All Dates, and both windows are legitimately
 * clearable here. (Contrast Fee Receipts, where a cleared payment window means
 * an all-time run with WRONG figures, so All is suppressed — CC-50020.)
 */
const buildFields = (usePosting) => [
  objectField({ key: 'client', label: 'Client', typeId: TYPE_COMPANIES, idKey: 'companyId', nameKey: 'companyName' }),
  // CC-49949: V1 calls this lookup "Professional" (a single user; the filter's userId).
  objectField({ key: 'professional', label: 'Professional', typeId: TYPE_USERS, idKey: 'userId', nameKey: 'userName' }),
  dateRangeField({ key: 'invoiceDates', label: 'Invoice Date', startKey: 'invoiceDateStart', endKey: 'invoiceDateEnd' }),
  // V1's label — the window rides paymentReceivedDateStart/End.
  dateRangeField({ key: 'paymentDates', label: 'Payment Allocated Date', startKey: 'paymentReceivedDateStart', endKey: 'paymentReceivedDateEnd' }),
  ...(usePosting
    ? [choiceField({
        key: 'posted', label: 'Posted', valueKey: 'postedFilter',
        options: POSTED_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
        // bool? tri-state: null = don't filter, which is NOT the same as false.
        encode: (v) => (v == null ? 'all' : v ? 'posted' : 'unposted'),
        decode: (s) => (s === 'all' ? null : s === 'posted'),
      })]
    : []),
  choiceField({
    key: 'view', label: 'View', valueKey: 'isSummaryReport',
    options: [{ value: 'summary', label: 'Summary' }, { value: 'detail', label: 'Detail' }],
    encode: (v) => (v ? 'summary' : 'detail'),
    decode: (s) => s === 'summary',
  }),
  boolField({ key: 'histTitles', label: 'Use Historical Titles', valueKey: 'useHistoricalTitles' }),
  boolField({ key: 'histOrig', label: 'Use Historical Origination', valueKey: 'useHistoricalOrigination' }),
]

// CC-49949: the wire is V1's RevenueAndCommissionsReportFilter, field for
// field (Endpoint/MesaUtilitiesBase/AccountingModels.cs:866 — the class Fee
// Receipts shares). Newtonsoft silently drops unknown keys and silently
// defaults missing ones, so send EXACTLY the keys the class declares:
// - `dateType` is deliberately ABSENT — it binds to FilterDateType but is read
//   nowhere on this path (the old "Date Type" select was a dead control,
//   CC-50020 item 3); omission yields the harmless ctor default.
// - `usePartners` / `billingPartnerFieldId` are ABSENT — PopulateData
//   overwrites both from server settings on every run.
// - empty dates go as null ('' does not bind to DateTime?).
const wireFilters = (f) => ({
  companyId: f.companyId,
  companyName: f.companyName || null,
  userId: f.userId,
  userName: f.userName || null,
  invoiceDateStart: f.invoiceDateStart || null,
  invoiceDateEnd: f.invoiceDateEnd || null,
  paymentReceivedDateStart: f.paymentReceivedDateStart || null,
  paymentReceivedDateEnd: f.paymentReceivedDateEnd || null,
  isSummaryReport: !!f.isSummaryReport,
  postedFilter: f.postedFilter,
  useHistoricalTitles: !!f.useHistoricalTitles,
  useHistoricalOrigination: !!f.useHistoricalOrigination,
})

// Module-level so useLatestRun's `run` keeps one identity (spec 131).
const runErrorMessage = (err) => err?.response?.data?.error || err.message || 'Report failed'

const RevenueCommissionsReport = ({ reportId, name = 'Production & Origination', summary, jobId }) => {
  // CC-49949: V1 parity (RevenueAndCommissionsHomeView / Home.cshtml) —
  // Invoice Date opens at All Dates, "Payment Allocated Date" (V1's label; the
  // markup notes "we're treating payment received as allocated") opens at This
  // Month, and the posted default resolves per the tenant below. V1 persists
  // the view mode + historical toggles per user in the server REGISTRY
  // (GetViewMode / GetUseHistoricalTitles / GetUseHistoricalOrigination);
  // reading those back would need a new cb-api proxy route, so cb2 uses V1's
  // virgin defaults instead — documented residual on the ticket.
  const [filters, setFilters] = useState(makeDefaults)
  // Spec 131: only the NEWEST run may write the rows, the spinner, the error or
  // the documents' criteria (the split button's mid-run "Run report now" is also
  // disabled now; this gate is the structural guarantee).
  const {
    running, error, ran, runKey, run: startRun,
  } = useLatestRun({ initialRunning: !!jobId, errorMessage: runErrorMessage })
  // CC-50013 (G3): saved views. The hook carries the grid's page state, so
  // the menu cannot be mounted without it (the CC-49732 defect).
  const view = useSavedReportView(setFilters)
  // CC-50029: exports state the period they cover.
  const filtersSummary = useMemo(() => describeFilters(dateRangePart(filters.paymentReceivedDateStart, filters.paymentReceivedDateEnd)), [filters])
  const [data, setData] = useState({ headers: [], rows: [], meta: [] })
  const [savedAsOf, setSavedAsOf] = useState(null)
  // Consume-once: the first run reopens the tray's saved job; later runs are fresh.
  const jobRef = useRef(jobId || null)

  // CC-49949 (same pattern as Aged A/R, CC-49752): V1 shows the Posted control
  // ONLY on posting tenants and defaults it to "Posted" there. The first
  // auto-run waits for the resolution so run 1 already carries the default.
  const { ready: usePostingReady, usePosting } = useUsePosting()
  const [postedDefaultApplied, setPostedDefaultApplied] = useState(false)
  useEffect(() => {
    if (!usePostingReady || postedDefaultApplied) return
    if (usePosting) {
      // The Posted select now derives its value from filters.postedFilter, so
      // there is one source of truth instead of a mirrored UI state.
      setFilters((f) => (f.postedFilter === null ? { ...f, postedFilter: true } : f))
    }
    // Batched with the setFilters above — the gated auto-run fires once, in
    // the commit that already carries the applied default.
    setPostedDefaultApplied(true)
  }, [usePostingReady, usePosting, postedDefaultApplied])

  const runReport = useCallback(() => startRun(async () => {
    const jid = jobRef.current
    jobRef.current = null // consumed — subsequent runs are fresh
    if (jid) {
      // Tray "View report" → reopen the exact saved snapshot + show its run time.
      const [st, r] = await Promise.all([reportJobService.status(jid), reportJobService.result(jid)])
      return () => { setSavedAsOf(st?.createdAt || null); setData(r?.data || { headers: [], rows: [], meta: [] }) }
    }
    const result = await specializedReportService.revenueCommissions.run(wireFilters(filters), { reportId })
    // Spec 131 decision 3: the saved-result banner clears at the COMMIT.
    return () => { setSavedAsOf(null); setData(result || { headers: [], rows: [], meta: [] }) }
  }, { criteria: { filtersSummary } }), [filters, reportId, startRun, filtersSummary])

  // Spec 131: deferred and cancellable, the shape every effect-driven run on
  // these pages now has, so an effect pass torn down before the timer fires
  // starts no run.
  useEffect(() => {
    if (!postedDefaultApplied) return undefined
    const t = setTimeout(() => { runReport() }, 0)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postedDefaultApplied])

  // CC-50013 G2 / CC-50148: shared metadata-driven bar. The wire is untouched —
  // wireFilters() below is still the single boundary, including the three keys it
  // deliberately OMITS (dateType / usePartners / billingPartnerFieldId).
  const fields = useMemo(() => buildFields(usePosting), [usePosting])
  const filterBar = (
    <SpecializedFilterBar
      fields={fields}
      filters={filters}
      onFiltersChange={setFilters}
      defaults={makeDefaults()}
      onRun={runReport}
      isLoading={running}
      queue={{
        reportId,
        name,
        actionKey: 'revenue-commissions',
        endpoint: (f) => ({ method: 'POST', path: '/reports/revenue-commissions', body: wireFilters(f) }),
        // Spec 202: its emailed file carries the run's own percent correction.
        attachFormats: specializedAttachFormats('/reports/revenue-commissions'),
      }}
      actions={(
        <ReportViewsMenu
          storageKey="report:revenue-commissions"
          currentFilters={filters}
          onApply={view.applyView}
          getPageState={view.getPageState}
        />
      )}
    />
  )

  return (
    <SpecializedReportShell
      name={name} summary={summary} running={running} error={error}
      ranCriteria={ran?.criteria ?? null} runKey={runKey}
      filterBar={filterBar} filtersSummary={filtersSummary} summaryData={data} savedAsOf={savedAsOf}
      download={(format, getToken) =>
        specializedReportService.revenueCommissions.download(wireFilters(filters), format, getToken, `revenue-commissions.${format}`)
      }
    >
      <CsvReportGrid key={view.gridKey} data={data} storageKey="report:revenue-commissions" pageStateRef={view.pageStateRef} initialPageState={view.initialPageState} />
    </SpecializedReportShell>
  )
}

export default RevenueCommissionsReport
